import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { FormattedMessage } from "react-intl";
import { getCourse } from "@/lib/data/courses";
import { useAuthStore } from "@/lib/stores/authStore";
import { useCourseNotesStore } from "@/lib/stores/courseNotesStore";
import { useLocale } from "@/contexts/IntlContext";
import { pickLocalized } from "@/lib/utils/localized";
import { CourseNotesList } from "@/components/trainer/CourseNotesList";

export const Route = createFileRoute("/trainer/notes/$courseSlug")({
  component: CourseNotes,
  head: () => ({ meta: [{ title: "My notes - HAMD" }] }),
});

const NO_NOTES: Record<string, string> = {};

function CourseNotes() {
  const { courseSlug } = Route.useParams();
  const course = getCourse(courseSlug);
  const { locale } = useLocale();

  const isTrainer = useAuthStore((s) => s.user?.accountType === "TRAINER");
  const notes = useCourseNotesStore(
    (s) => s.notesByCourse[courseSlug] ?? NO_NOTES,
  );
  const loadNotes = useCourseNotesStore((s) => s.loadNotes);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isTrainer || !course) {
      setLoading(false);
      return;
    }
    let alive = true;
    loadNotes(courseSlug).finally(() => {
      if (alive) setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [isTrainer, course, courseSlug, loadNotes]);

  if (!course) {
    return (
      <main className="px-4 py-10 text-center">
        <p className="text-body text-dark-gray">
          <FormattedMessage id="trainer.course.notFound" />
        </p>
        <Link
          to="/trainer/notes"
          className="mt-3 inline-block text-small font-medium text-primary"
        >
          <FormattedMessage id="trainer.notes.backToNotes" />
        </Link>
      </main>
    );
  }

  return (
    <main className="space-y-5 px-4 pb-6">
      <header className="space-y-1">
        <h1 className="text-h1 font-medium text-black">
          {pickLocalized(course.title, locale)}
        </h1>
        <p className="text-small text-dark-gray">
          <FormattedMessage id="trainer.notes.title" />
        </p>
      </header>

      {!isTrainer ? (
        <div className="rounded-2xl bg-secondary/40 p-4">
          <p className="text-small text-dark-gray">
            <FormattedMessage id="trainer.notes.guestPrompt" />
          </p>
        </div>
      ) : loading ? (
        <p className="text-small text-dark-gray">
          <FormattedMessage id="trainer.notes.loading" />
        </p>
      ) : (
        <CourseNotesList course={course} notes={notes} />
      )}
    </main>
  );
}
